import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Typography,
} from '@mui/material'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import { supabase } from '../../supabaseClient'

// Communications — the messages a class gets sent more than once.
//
// Staff already have a free-form "Message class" box. The recurring ones get
// retyped each term from memory, which is how a detail like "finish Step 1
// before the first session" quietly drops out of the welcome.
//
// A template is a starting point, never a send: picking one hands the subject
// and body to the existing compose dialog, where it is edited and sent through
// send-class-message like any other message. Nothing here sends by itself, and
// nothing here bypasses the confirmation the compose dialog already asks for.

export interface CommTemplate {
  template_id: string
  label:       string
  subject:     string
  body:        string
}

export default function ClassCommunicationsSection({
  classId, editable, onUse,
}: {
  classId: string
  editable: boolean
  /** Hands the chosen template to the page's compose dialog. */
  onUse: (t: CommTemplate) => void
}) {
  const [rows,    setRows]    = useState<CommTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    /* Templates for every class, plus any belonging to this one. */
    const { data, error: e } = await supabase
      .from('class_communication_template')
      .select('template_id, label, subject, body, class_id, sort_order')
      .eq('active', true)
      .or(`class_id.is.null,class_id.eq.${classId}`)
      .order('sort_order')
      .order('label')
    setLoading(false)
    if (e) { setError(e.message); return }
    setRows((data ?? []) as CommTemplate[])
  }, [classId])

  useEffect(() => { load() }, [load])

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Communications</Typography>
        <Chip size="small" label={rows.length} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No templates yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {rows.map(t => (
            <Paper key={t.template_id} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
                  {t.subject}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EmailOutlinedIcon />}
                  disabled={!editable}
                  onClick={() => onUse(t)}
                >
                  Use
                </Button>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block', mt: 1, whiteSpace: 'pre-wrap',
                  maxHeight: 72, overflow: 'hidden',
                }}
              >
                {t.body}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Picking one opens the message composer with its wording, for you to edit before sending.
        [Student] becomes the recipient&rsquo;s first name.
      </Typography>
    </Box>
  )
}
